import React, { useEffect, useState } from 'react'
import { FormGroup, FormLabel } from 'react-bootstrap'
import * as StripeElements from 'react-stripe-elements'
import { stripeToken } from '../../../modules/token'
import { Button } from '@/features/shadcn/ui/button'
import type { CreateOrgPlanSelection } from './CreateOrganizationWizard'

const {
  injectStripe,
  StripeProvider,
  Elements,
  CardNumberElement,
  CardExpiryElement,
} = StripeElements

const CardCVCElement =
  (StripeElements as any).CardCVCElement || (StripeElements as any).CardCvcElement

const CONTENT_STYLE = {
  base: {
    color: '#32325d',
    lineHeight: '18px',
    fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
    fontSmoothing: 'antialiased',
    fontSize: '16px',
    '::placeholder': {
      color: '#aab7c4',
    },
  },
  invalid: {
    color: '#a94442',
    iconColor: '#a94442',
  },
}

interface CheckoutFormProps {
  stripe: { createToken: () => Promise<{ error?: { message: string }; token?: { id: string } }> }
  plan: CreateOrgPlanSelection
  businessPrice: number
  entrepreneurPrice: number
  submitting: boolean
  error: string | null
  onSubmit: (stripeToken: string) => void
}

function CheckoutForm({
  stripe,
  plan,
  businessPrice,
  entrepreneurPrice,
  submitting,
  error,
  onSubmit,
}: CheckoutFormProps) {
  const [cardError, setCardError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setCardError(null)
    const data = await stripe.createToken()
    if (data.error) {
      setCardError(data.error.message)
      return
    }
    if (data.token?.id) {
      onSubmit(data.token.id)
    }
  }

  const priceLabel =
    plan.product === 'Business'
      ? `$${businessPrice}/mo`
      : plan.product === 'Entrepreneur'
        ? `$${entrepreneurPrice}/mo per user`
        : 'Free'

  return (
    <form onSubmit={(e) => void handleSubmit(e)} className="tw-space-y-4">
      <div className="tw-rounded-lg tw-border tw-border-slate-200 tw-bg-slate-50/80 tw-px-4 tw-py-3">
        <p className="tw-m-0 tw-text-sm tw-text-slate-600">
          Subscribe to <span className="tw-font-semibold tw-text-slate-900">{plan.product}</span>{' '}
          ({priceLabel}) for this organization. Your card will be charged before the workspace is
          created.
        </p>
      </div>

      <div className="credit-card-form tw-space-y-3">
        <FormGroup className="tw-mb-0">
          <FormLabel className="tw-text-sm tw-font-medium tw-text-slate-800">Card number</FormLabel>
          <div className="tw-rounded-md tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-2.5">
            <CardNumberElement placeholder="4242 4242 4242 4242" style={CONTENT_STYLE} />
          </div>
        </FormGroup>
        <div className="tw-grid tw-grid-cols-2 tw-gap-3">
          <FormGroup className="tw-mb-0">
            <FormLabel className="tw-text-sm tw-font-medium tw-text-slate-800">Expiry</FormLabel>
            <div className="tw-rounded-md tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-2.5">
              <CardExpiryElement style={CONTENT_STYLE} />
            </div>
          </FormGroup>
          <FormGroup className="tw-mb-0">
            <FormLabel className="tw-text-sm tw-font-medium tw-text-slate-800">CVC</FormLabel>
            <div className="tw-rounded-md tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-2.5">
              <CardCVCElement placeholder="CVC" style={CONTENT_STYLE} />
            </div>
          </FormGroup>
        </div>
      </div>

      {(cardError || error) && (
        <p className="tw-rounded-md tw-bg-red-50 tw-px-3 tw-py-2 tw-text-sm tw-text-red-700">
          {cardError || error}
        </p>
      )}

      <Button type="submit" className="tw-w-full" disabled={submitting}>
        {submitting ? 'Processing payment…' : 'Subscribe & create organization'}
      </Button>
    </form>
  )
}

const StripeCheckoutForm = injectStripe(CheckoutForm)

interface CreateOrganizationCheckoutProps {
  plan: CreateOrgPlanSelection
  businessPrice: number
  entrepreneurPrice: number
  submitting: boolean
  error: string | null
  onSubmit: (stripeToken: string) => void
}

export default function CreateOrganizationCheckout(props: CreateOrganizationCheckoutProps) {
  const [stripeLoaded, setStripeLoaded] = useState(!!window.Stripe)

  useEffect(() => {
    if (stripeLoaded) return
    const script = document.createElement('script')
    script.src = 'https://js.stripe.com/v3/'
    script.onload = () => setStripeLoaded(true)
    document.head.appendChild(script)
    return () => {
      script.remove()
    }
  }, [stripeLoaded])

  if (!stripeToken) {
    return (
      <p className="tw-text-sm tw-text-red-700">
        Stripe is not configured. Paid organization plans are unavailable.
      </p>
    )
  }

  if (!stripeLoaded) {
    return <p className="tw-text-sm tw-text-slate-500">Loading payment form…</p>
  }

  return (
    <StripeProvider apiKey={stripeToken}>
      <Elements>
        <StripeCheckoutForm {...props} />
      </Elements>
    </StripeProvider>
  )
}
